/**
 * One page_view per logical navigation (initial load, PUSH, POP).
 *
 * REPLACE navigations are URL rewrites of the current page (legacy <Navigate replace>,
 * SEO canonical/query cleanup, filter and city changes on a listing — those are covered
 * by search/filter events). They never start a new page_view; before the pending view
 * is sent they only retarget it to the final URL. Counting never depends on how fast
 * the API answers — the settle window only decides whether a fast redirect reports its
 * final URL.
 */
export interface PageviewSchedulerOptions {
  send: (path: string) => void
  settleMs: number
  schedule?: (fn: () => void, ms: number) => unknown
  cancel?: (handle: unknown) => void
}

export interface PageviewScheduler {
  navigate: (historyAction: string, path: string) => void
  flush: () => void
}

export function createPageviewScheduler({
  send,
  settleMs,
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}: PageviewSchedulerOptions): PageviewScheduler {
  let pendingPath: string | null = null
  let timer: unknown

  const clearTimer = () => {
    if (timer !== undefined) cancel(timer)
    timer = undefined
  }

  const flush = () => {
    clearTimer()
    if (pendingPath === null) return
    const path = pendingPath
    pendingPath = null
    send(path)
  }

  const arm = (path: string) => {
    pendingPath = path
    clearTimer()
    timer = schedule(flush, settleMs)
  }

  return {
    navigate(historyAction, path) {
      if (historyAction !== 'REPLACE') {
        flush()
        arm(path)
        return
      }
      if (pendingPath !== null) arm(path)
    },
    flush,
  }
}
